import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  MobileHeader, DateNavigator, StatTiles, Card, Alert,
  EmptyState, ErrorState, CollapsibleSection,
} from '@parsina/framework';
import { CalendarDays, UserCheck, CalendarClock, CheckCircle2, Info } from 'lucide-react';
import { C, CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { useAppNavigate } from '../../hooks/useAppNavigate.js';
import { useClinicScope } from '../../hooks/useClinicScope.jsx';
import { useMe } from '../../hooks/useMe.js';
import { useToday } from '../../hooks/useToday.js';
import { useNextService } from '../../hooks/useNextService.js';
import { buildDayModel, dayCounts } from '../../lib/appointments.js';
import { todayInIran, shiftISO, isoDayToMs, nowMinutesInIran, ISO_DATE_RE } from '../../lib/iranTime.js';
import MobileAppointmentCard from '../../components/mobile/MobileAppointmentCard.jsx';
import MobileNextPatient from '../../components/mobile/MobileNextPatient.jsx';
import { ShowMore, MobileSectionTitle } from '../../components/mobile/MobileKit.jsx';
import { TodaySkeletonMobile } from './Today.skeleton.jsx';

// امروز — THE MOBILE COCKPIT (DESIGN-CONCEPT-MOBILE §4).
//
// ┌──────────────────────────────────────┐
// │ ① MobileHeader (gradient) — PINNED    │  avatar · greeting · name
// │    └ DateNavigator (white-alpha card) │  ‹ | 🗓 شنبه، ۲۶ تیر | ›
// │ ② StatTiles — 4-up, riding the edge   │  امروز · اینجا · نوبت‌دار · انجام‌شده
// ├──────────────────────────────────────┤  ── the scroll starts here ──
// │ ③ Alert — only off today              │
// │ ④ MobileNextPatient (hero)            │
// │ ⑤ three sections of cards             │
// │ ⑥ the read-only note                  │
// │ [ BottomNav ]                          │
// └──────────────────────────────────────┘
//
// The header, the date navigator and the tiles are PINNED and the card list is
// what scrolls — §3.2's rule for a glanceable screen: "the day's numbers and
// the date navigator never leave the screen".
//
// NO quick actions in the header and NO sticky CTA. §3.2 and §10 are explicit
// that "a control that navigates nowhere is a defect, not a placeholder" — this
// screen writes nothing at all (booking, cancelling and queue order belong to
// the clinic desk), so a gradient CTA here would be a button with no server
// behind it. اعتبار now exists and IS a destination, but it belongs in the
// bottom bar with the other three, not as a shortcut on top of the day sheet:
// nothing about a day's appointments is a reason to go and buy پارسه.

// How many cards a section shows before «نمایش بیشتر».
const PAGE = 5;

const Section = ({ id, title, count, children, defaultOpen = true, collapsible }) => {
  const body = <div style={{ paddingTop: '0.25rem' }}>{children}</div>;
  if (!collapsible) {
    return (
      <section data-testid={`today-section-${id}`} style={{ marginBottom: '0.875rem' }}>
        <MobileSectionTitle count={count}>{title}</MobileSectionTitle>
        {body}
      </section>
    );
  }
  return (
    <section data-testid={`today-section-${id}`} style={{ marginBottom: '0.875rem' }}>
      <CollapsibleSection title={title} badge={count} defaultOpen={defaultOpen}>
        {body}
      </CollapsibleSection>
    </section>
  );
};

/** One section's cards, capped and grown by an explicit expander (§4). */
const CardList = ({ id, rows, model, tick, onOpen }) => {
  const [shown, setShown] = React.useState(PAGE);
  // A new day (or a poll that shortened the list) must not leave a stale
  // "expanded to 20" behind.
  React.useEffect(() => setShown(PAGE), [id, rows.length === 0]);
  return (
    <>
      {rows.slice(0, shown).map((apt) => (
        <MobileAppointmentCard
          key={apt.id}
          apt={apt}
          queuePos={model.queuePosOf.get(apt.id) ?? null}
          nowMs={tick}
          onOpen={onOpen}
          testId={`today-row-${apt.id}`}
        />
      ))}
      {rows.length > shown && (
        <ShowMore onClick={() => setShown((n) => n + PAGE)} testId={`today-more-${id}`} />
      )}
    </>
  );
};

const TodayMobile = () => {
  const { t, formatNumber, formatDate } = useTranslation();
  const navigate = useAppNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeId } = useClinicScope();
  const { me } = useMe();

  // The `?date=` contract is the desktop's, verbatim — a link shared from a
  // phone opens the same day on a workstation.
  const rawDate = searchParams.get('date');
  const today = todayInIran();
  const selected = ISO_DATE_RE.test(rawDate || '') ? rawDate : today;
  const isToday = selected === today;

  const setDay = React.useCallback(
    (iso) => {
      const next = new URLSearchParams(searchParams);
      if (!iso || iso === todayInIran()) next.delete('date');
      else next.set('date', iso);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const { appointments, queue, loading, error, reload } = useToday({
    date: selected,
    clinicId: activeId,
  });

  const [tick, setTick] = React.useState(() => Date.now());
  React.useEffect(() => {
    const timer = setInterval(() => setTick(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, []);

  const model = React.useMemo(
    () =>
      buildDayModel({
        appointments,
        queue,
        isToday,
        nowMin: nowMinutesInIran(tick) ?? 0,
      }),
    [appointments, queue, isToday, tick],
  );
  // `counted`, not `dayApts` — the day sheet PLUS anyone in the room who is not
  // on it. lib/appointments.js:98-114 explains why the two differ; a tile
  // reading «اینجا ۰» above a populated waiting room is the screen
  // contradicting itself.
  const counts = React.useMemo(() => dayCounts(model.counted), [model.counted]);
  const hasRows = model.counted.length > 0;

  // What the next patient is coming FOR. Read beside the day, on the PATIENT
  // changing rather than on the day poll — see hooks/useNextService.js.
  const nextService = useNextService({
    patientId: model.nextApt?.patientId || null,
    clinicId: activeId,
  });

  // A day-sheet card has ONE tap target on this surface, so it opens the thing
  // the dentist came for: جلسهٔ درمان for THIS appointment. The patient's whole
  // record is one tap further in, from the visit header's own action — the
  // reverse of the desktop rail, which has room for both buttons on the row.
  //
  // A «ثبت سریع» walk-in has no patient record and the workspace cannot open on
  // one, so its card falls back to doing nothing rather than navigating to a
  // screen that would immediately render «جلسه یافت نشد».
  const openVisit = React.useCallback(
    (apt) => {
      if (apt?.id && apt?.patientId) navigate('visit', { appointmentId: apt.id });
    },
    [navigate],
  );

  // The hero is the exception to "one tap target": it has the room for the
  // second one, so «پرونده» there means the patient's record, as it does on the
  // desktop hero — not a second door onto the visit.
  const openRecord = React.useCallback(
    (apt) => {
      if (apt?.patientId) navigate('patient', { id: apt.patientId });
    },
    [navigate],
  );

  // EXACTLY FOUR TILES, always (§4). The desktop's conditional fifth KPI
  // («لغو شده», rendered only when there are any) becomes a muted line under
  // the row — the 4-up rhythm is the design's signature and a row that is
  // sometimes five wide is a different component.
  const tiles = [
    {
      key: 'total',
      label: t('today.counts.total'),
      value: formatNumber(counts.total),
      icon: <CalendarDays size={16} aria-hidden />,
      solid: true,
    },
    {
      key: 'here',
      label: t('today.counts.here'),
      value: formatNumber(counts.here),
      icon: <UserCheck size={16} aria-hidden />,
      color: C.teal,
    },
    {
      key: 'scheduled',
      label: t('today.counts.scheduled'),
      value: formatNumber(counts.scheduled),
      icon: <CalendarClock size={16} aria-hidden />,
      color: C.navy,
    },
    {
      key: 'completed',
      label: t('today.counts.completed'),
      value: formatNumber(counts.completed),
      icon: <CheckCircle2 size={16} aria-hidden />,
      color: CT.goodInk,
    },
  ];

  return (
    <>
      {/* ── ① + ② the pinned zone. Outside the scroll region on purpose. ── */}
      <div style={{ flexShrink: 0 }}>
        <MobileHeader
          user={{ name: me?.fullName || '' }}
          // The kit defaults `greeting` to a Persian literal; this app ships en.
          greeting={t('mobile.greeting')}
          data-testid="today-header"
        >
          <DateNavigator
            value={selected}
            onChange={(v) => setDay(v || today)}
            dateLabel={formatDate(isoDayToMs(selected), 'full')}
            isToday={isToday}
            onPrev={() => setDay(shiftISO(selected, -1))}
            onNext={() => setDay(shiftISO(selected, 1))}
            onToday={() => setDay(today)}
            prevLabel={t('today.date.prev')}
            nextLabel={t('today.date.next')}
            pickLabel={t('today.date.pick')}
            todayLabel={t('today.date.today')}
            sheetTitle={t('today.date.pick')}
            closeLabel={t('mobile.close')}
            data-testid="today-datenav"
          />
        </MobileHeader>

        <div style={{ padding: '0 0.75rem', marginTop: '-1rem', position: 'relative' }}>
          <StatTiles tiles={tiles} loading={loading} data-testid="today-stats" />
          {counts.cancelled > 0 && (
            <div
              data-testid="today-cancelled-line"
              style={{
                marginTop: '0.375rem',
                fontSize: '0.6875rem',
                color: C.textMuted,
                textAlign: 'center',
              }}
            >
              {t('today.counts.cancelledLine', { count: counts.cancelled })}
            </div>
          )}
        </div>
      </div>

      {/* ── the ONE page scroll ── */}
      <div
        data-testid="today-content"
        data-state={error ? 'error' : loading ? 'loading' : hasRows ? 'content' : 'empty'}
        aria-busy={loading || undefined}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          // Clear of the bottom bar so the last card is never covered (§4).
          padding: '0.875rem 0.75rem 1.5rem',
        }}
      >
        {error ? (
          // The header and the date navigator stay MOUNTED and operable through
          // the failure (§7) — a failed day is one tap from a good one.
          <div role="alert">
            <ErrorState
              title={t('today.error.title')}
              description={t('errors.load.today')}
              onRetry={reload}
              retryLabel={t('common.actions.retry')}
            />
          </div>
        ) : loading ? (
          // The cockpit's OWN shape: the next-patient hero and the first
          // section of cards at their real size (§7 — never a spinner, never a
          // generic card stack). The pinned header and tiles above are real.
          <TodaySkeletonMobile label={t('common.loading')} />
        ) : (
          <>
            {/* The live queue exists only for today; `buildDayModel` returns an
                EMPTY roomQueue and a null nextApt off today, so this notice is
                a fact about the data and not a disclaimer. */}
            {!isToday && (
              <Alert variant="info" style={{ marginBottom: '0.875rem' }}>
                {t('today.notice.liveQueue')}
              </Alert>
            )}

            {!hasRows ? (
              <Card>
                <EmptyState
                  icon={<CalendarDays size={44} color={C.textMuted} />}
                  title={t('today.empty.title')}
                  description={t('today.empty.description')}
                />
              </Card>
            ) : (
              <>
                <MobileNextPatient
                  apt={model.nextApt}
                  service={nextService}
                  onOpen={openRecord}
                  onOpenVisit={openVisit}
                />

                <div style={{ marginTop: '0.875rem' }}>
                  {model.roomQueue.length > 0 && (
                    <Section
                      id="queue"
                      title={t('today.blocks.queue')}
                      count={formatNumber(model.roomQueue.length)}
                    >
                      <CardList
                        id="queue"
                        rows={model.roomQueue}
                        model={model}
                        tick={tick}
                        onOpen={openVisit}
                      />
                    </Section>
                  )}

                  {model.upcoming.length > 0 && (
                    <Section
                      id="upcoming"
                      title={t('today.blocks.upcoming')}
                      count={formatNumber(model.upcoming.length)}
                    >
                      <CardList
                        id="upcoming"
                        rows={model.upcoming}
                        model={model}
                        tick={tick}
                        onOpen={openVisit}
                      />
                    </Section>
                  )}

                  {model.done.length > 0 && (
                    <Section
                      id="done"
                      title={t('today.blocks.done')}
                      count={formatNumber(model.done.length)}
                      collapsible
                      defaultOpen={model.roomQueue.length === 0 && model.upcoming.length === 0}
                    >
                      <CardList
                        id="done"
                        rows={model.done}
                        model={model}
                        tick={tick}
                        onOpen={openVisit}
                      />
                    </Section>
                  )}
                </div>
              </>
            )}

            {/* Says out loud what this screen does NOT do, so its missing
                buttons read as a boundary rather than as an omission (§1). */}
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5rem',
                marginTop: '1rem',
                color: C.textMuted,
                fontSize: '0.6875rem',
                lineHeight: 1.8,
              }}
            >
              <Info size={14} style={{ flexShrink: 0, marginTop: '0.125rem' }} aria-hidden />
              <span>{t('today.readOnlyNote')}</span>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default TodayMobile;
